import {useEffect, useState} from 'react';
import axios from 'axios';
import {Button} from '@/components/ui/button';
import {Badge} from '@/components/ui/badge';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Switch} from '@/components/ui/switch';
import {Textarea} from '@/components/ui/textarea';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog';
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card';
import {toast} from 'sonner';
import {useAuth} from '@/context/AuthContext';
import {ITEM_TAGS, ITEM_TAG_STYLES} from '@/constants/itemTags';
import {Plus, Edit, Trash2, Upload, Search, X, TicketPercent, TicketX, Download, FileSpreadsheet, FileText, Loader2, BadgePercent, PackageX, PackageCheck} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {exportToExcel, exportToCsv} from '@/lib/exportExcel';
import CategoryCommissionDialog from './CategoryCommissionDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same rounding as the customer API (foodk_api calculateCommission).
const commissionAmount = (price, type, value) => {
    const p = Number(price);
    const v = Number(value);
    if (!Number.isFinite(p) || !type || !v) return 0;
    const t = String(type).toUpperCase();
    if (t === 'PERCENTAGE') return Math.round((p * v) / 100);
    if (t === 'FLAT' || t === 'FIXED') return Math.round(v);
    return 0;
};
// Validation for item commission inputs ('DEFAULT' = inherit).
const commissionValueError = (type, raw) => {
    if (!type || type === 'DEFAULT') return null;
    if (raw === '' || raw === null || raw === undefined) return 'Enter a value';
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0) return 'Enter a valid value';
    if (type === 'PERCENTAGE' && v > 100) return 'Percentage can be at most 100';
    if (type === 'FLAT' && v > 10000) return 'Flat commission can be at most ₹10,000';
    return null;
};
const formatRupee = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}`;

const ItemsTab = ({restaurantId}) => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [showItemDialog, setShowItemDialog] = useState(false);
    const [showVariantDialog, setShowVariantDialog] = useState(false);
    const [showAddonDialog, setShowAddonDialog] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [openBulk, setOpenBulk] = useState(false);
    const [file, setFile] = useState(null);
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    // Coupon applicability: ids currently being saved + "blocked only" filter
    const [couponSavingIds, setCouponSavingIds] = useState([]);
    const [couponBlockedOnly, setCouponBlockedOnly] = useState(false);
    const [exporting, setExporting] = useState(false);
    // In-stock toggle: ids being saved
    const [availabilitySavingIds, setAvailabilitySavingIds] = useState([]);
    const {can} = useAuth();
    const canEditItems = can('items', 'edit');
    // Item form: search box for the "Suggest with this item" picker
    const [linkedSearch, setLinkedSearch] = useState('');
    // Commission: restaurant's own + active category rules (Commission Management)
    const [restaurantCommission, setRestaurantCommission] = useState({type: null, value: 0});
    const [commissionRules, setCommissionRules] = useState(new Map());
    // Category commission dialog (null = closed; 'all' or a categoryId to focus)
    const [commissionDialog, setCommissionDialog] = useState(null);
    // Bulk item-wise commission for the selected items
    const [itemCommissionOpen, setItemCommissionOpen] = useState(false);
    const [itemCommissionForm, setItemCommissionForm] = useState({type: 'PERCENTAGE', value: ''});
    const [itemCommissionSaving, setItemCommissionSaving] = useState(false);

    const [itemForm, setItemForm] = useState({
        name: '',
        description: '',
        foodType: 'VEG',
        categoryId: '',
        cuisines: '',
        price: '',
        mrpPrice: '',
        commissionType: 'DEFAULT',
        commissionValue: '',
        hasVariants: false,
        isAvailable: true,
        isRecommended: false,
        recommendedItemIds: [],
        catOffer: false,
        storenintynine: false,
        storetwofifty: false,
        couponApplicable: true,
        noOnionGarlic: false,
        tag: 'none',
        priority: 0
    });

    const [variantForm, setVariantForm] = useState({
        title: '',
        foodType: 'VEG',
        price: '',
        mrpPrice: '',
        isDefault: false
    });

    const [addonForm, setAddonForm] = useState({
        title: '',
        foodType: 'VEG',
        price: '',
        mrpPrice: ''
    });

    const [imageFile, setImageFile] = useState(null);

    useEffect(() => {
        fetchItems();
        fetchCategories();
        fetchCommission();
    }, [restaurantId]);

    const fetchCommission = async () => {
        if (!restaurantId) return;
        try {
            const [restRes, rulesRes] = await Promise.all([
                axios.get(`${API}/restaurant-profile/${restaurantId}/profile`),
                axios.get(`${API}/commissions`, {params: {restaurantId, status: 'active', limit: 200}})
                    .catch(() => ({data: {data: []}}))
            ]);
            const r = restRes.data?.restaurant || restRes.data || {};
            setRestaurantCommission({type: r.commissionType || null, value: Number(r.commissionValue) || 0});
            setCommissionRules(new Map((rulesRes.data?.data || []).map((x) => [
                String(x.categoryId),
                {type: x.commissionType, value: Number(x.commissionValue) || 0, name: x.categoryName}
            ])));
        } catch (err) {
            console.error('Failed to fetch commission:', err);
        }
    };

    // Which commission applies to an item: its own commission, else the
    // sub-category rule, else the category rule, else the restaurant
    // commission (same order as the customer API).
    const commissionFor = (item) => {
        if (item && (item.commissionType === 'PERCENTAGE' || item.commissionType === 'FLAT')
            && item.commissionValue !== null && item.commissionValue !== undefined) {
            return {type: item.commissionType, value: Number(item.commissionValue) || 0, source: 'Item commission', own: true};
        }
        const sub = item?.subcategoryId ? commissionRules.get(String(item.subcategoryId)) : null;
        if (sub) return {...sub, source: `${sub.name || 'Sub-category'} rule`};
        const cat = item?.categoryId ? commissionRules.get(String(item.categoryId)) : null;
        if (cat) return {...cat, source: `${cat.name || 'Category'} rule`};
        return {...restaurantCommission, source: 'Restaurant commission'};
    };
    const commissionLabel = (c) =>
        !c?.type || !c?.value ? 'No commission'
            : String(c.type).toUpperCase() === 'PERCENTAGE' ? `${c.value}%` : `${formatRupee(c.value)} flat`;
    const customerPrice = (price, c) => Number(price || 0) + commissionAmount(price, c?.type, c?.value);

    const fetchCategories = async () => {
        if (!restaurantId) return;
        try {
            const res = await axios.get(`${API}/categories`, {
                params: {resturantId: restaurantId, limit: 200}
            });
            // Backend now returns { data, pagination } instead of a bare array.
            const raw = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
            // Only keep categories belonging to this restaurant + active
            const list = raw.filter(
                (c) =>
                    (c.resturantId === restaurantId ||
                        c.resturant?.id === restaurantId) &&
                    c.status !== false
            );
            setCategories(list);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    const fetchItems = async () => {
        try {
            const response = await axios.get(`${API}/items/restaurant/${restaurantId}`);
            setItems(response.data);
        } catch (error) {
            console.error('Failed to fetch items:', error);
            toast.error('Failed to fetch items');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            alert("Please select a file");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            await axios.post(`${API}/items/${restaurantId}/bulk_update_product`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            toast.success('Items imported successfully');
            setOpenBulk(false);
            setFile(null);
            await fetchItems();

        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || 'Upload Failed');
        }
    };

    const fetchItemDetails = async (itemId) => {
        try {
            const response = await axios.get(`${API}/items/${itemId}`);
            setSelectedItem(response.data);
        } catch (error) {
            console.error('Failed to fetch item details:', error);
            toast.error('Failed to fetch item details');
        }
    };

    const openCreateItemDialog = () => {
        setCurrentItem(null);
        setItemForm({
            name: '',
            description: '',
            foodType: 'VEG',
            categoryId: '',
            cuisines: '',
            price: '',
            mrpPrice: '',
            commissionType: 'DEFAULT',
            commissionValue: '',
            hasVariants: false,
            isAvailable: true,
            isRecommended: false,
            recommendedItemIds: [],
            catOffer: false,
            storenintynine: false,
            storetwofifty: false,
            couponApplicable: true,
            noOnionGarlic: false,
            tag: 'none',
            priority: 0
        });
        setImageFile(null);
        setLinkedSearch('');
        setShowItemDialog(true);
    };

    const openEditItemDialog = (item) => {
        setCurrentItem(item);
        setItemForm({
            name: item.name || '',
            description: item.description || '',
            foodType: item.foodType || 'VEG',
            categoryId: item.categoryId ? String(item.categoryId) : '',
            cuisines: Array.isArray(item.cuisines) ? item.cuisines.join(', ') : '',
            price: item.price || '',
            mrpPrice: item.mrpPrice || '',
            commissionType: item.commissionType === 'PERCENTAGE' || item.commissionType === 'FLAT' ? item.commissionType : 'DEFAULT',
            commissionValue: item.commissionType && item.commissionValue !== null && item.commissionValue !== undefined
                ? String(item.commissionValue) : '',
            hasVariants: item.hasVariants || false,
            isAvailable: item.isAvailable !== undefined ? item.isAvailable : true,
            isRecommended: item.isRecommended || false,
            recommendedItemIds: Array.isArray(item.recommendedItemIds) ? item.recommendedItemIds.map(String) : [],
            catOffer: item.catOffer || false,
            storenintynine: item.storenintynine || false,
            storetwofifty: item.storetwofifty || false,
            // Legacy items have no value — treat as applicable
            couponApplicable: item.couponApplicable !== false,
            noOnionGarlic: item.noOnionGarlic === true,
            tag: item.tag || 'none',
            priority: item.priority || 0
        });
        setImageFile(null);
        setLinkedSearch('');
        setShowItemDialog(true);
    };

    const handleItemSubmit = async (e) => {
        e.preventDefault();
        const itemCommissionError = commissionValueError(itemForm.commissionType, itemForm.commissionValue);
        if (itemCommissionError) {
            toast.error(`Item commission: ${itemCommissionError}`);
            return;
        }
        const formData = new FormData();

        formData.append('resturantId', restaurantId);
        formData.append('name', itemForm.name);
        formData.append('description', itemForm.description);
        formData.append('foodType', itemForm.foodType);
        if (itemForm.categoryId) {
            formData.append('categoryId', itemForm.categoryId);
        }
        formData.append('cuisines', itemForm.cuisines);
        formData.append('price', itemForm.price);
        formData.append('mrpPrice', itemForm.mrpPrice);
        // Item-wise commission — 'DEFAULT' = use category / restaurant commission
        formData.append('commissionType', itemForm.commissionType || 'DEFAULT');
        formData.append('commissionValue', itemForm.commissionType === 'DEFAULT' ? '' : itemForm.commissionValue);
        formData.append('hasVariants', itemForm.hasVariants);
        formData.append('isAvailable', itemForm.isAvailable);
        formData.append('isRecommended', itemForm.isRecommended);
        // Item-wise cart suggestions (always sent so clearing the list works)
        formData.append('recommendedItemIds', JSON.stringify(itemForm.recommendedItemIds || []));
        formData.append('storetwofifty', itemForm.storetwofifty);
        formData.append('storenintynine', itemForm.storenintynine);
        formData.append('catOffer', itemForm.catOffer);
        formData.append('couponApplicable', itemForm.couponApplicable);
        formData.append('noOnionGarlic', itemForm.noOnionGarlic === true);
        // 'none' clears the tag on the server
        formData.append('tag', itemForm.tag || 'none');
        formData.append('priority', itemForm.priority);

        if (imageFile) {
            formData.append('image', imageFile);
        }

        try {
            if (currentItem) {
                await axios.patch(`${API}/items/${currentItem.id}`, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                toast.success('Item updated successfully');
            } else {
                await axios.post(`${API}/items`, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                toast.success('Item created successfully');
            }
            setShowItemDialog(false);
            fetchItems();
        } catch (error) {
            console.error('Failed to save item:', error);
            toast.error('Failed to save item');
        }
    };

    const deleteItem = async (itemId) => {
        if (!window.confirm('Are you sure? This will delete all variants and addons too.')) return;

        try {
            await axios.delete(`${API}/items/${itemId}`);
            toast.success('Item deleted successfully');
            fetchItems();
        } catch (error) {
            console.error('Failed to delete item:', error);
            toast.error('Failed to delete item');
        }
    };

    const toggleSelectItem = (itemId) => {
        setSelectedItemIds((prev) =>
            prev.includes(itemId) ? prev.filter((x) => x !== itemId) : [...prev, itemId]
        );
    };

    const toggleSelectAllItems = () => {
        if (selectedItemIds.length === items.length) {
            setSelectedItemIds([]);
        } else {
            setSelectedItemIds(items.map((i) => i.id));
        }
    };

    const handleBulkDeleteItems = async () => {
        if (selectedItemIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedItemIds.length} selected item(s)? This will delete their variants and addons too.`)) return;

        try {
            setBulkDeleting(true);
            await axios.post(`${API}/items/bulk-delete`, { ids: selectedItemIds });
            toast.success(`${selectedItemIds.length} item(s) deleted`);
            setSelectedItemIds([]);
            fetchItems();
        } catch (error) {
            console.error('Failed to bulk delete items:', error);
            toast.error('Failed to delete selected items');
        } finally {
            setBulkDeleting(false);
        }
    };

    // Enable / disable coupon discounts for one or many items.
    // Updates the list optimistically and rolls back if the request fails.
    const isCouponApplicable = (item) => item.couponApplicable !== false;

    const updateCouponApplicable = async (ids, couponApplicable) => {
        if (!ids.length) return;
        const idSet = new Set(ids);
        // Remember each item's old value so a failure only reverts these items
        const previousValues = Object.fromEntries(
            items.filter((it) => idSet.has(it.id)).map((it) => [it.id, it.couponApplicable])
        );

        setCouponSavingIds((prev) => [...new Set([...prev, ...ids])]);
        setItems((prev) => prev.map((it) => (idSet.has(it.id) ? {...it, couponApplicable} : it)));

        try {
            await axios.patch(`${API}/items/coupon-applicable`, {
                ids,
                couponApplicable,
                restaurantId
            });
            toast.success(
                ids.length === 1
                    ? `Coupons ${couponApplicable ? 'enabled' : 'disabled'} for this item`
                    : `Coupons ${couponApplicable ? 'enabled' : 'disabled'} for ${ids.length} items`
            );
        } catch (error) {
            console.error('Failed to update coupon applicability:', error);
            setItems((prev) => prev.map((it) => (
                idSet.has(it.id) ? {...it, couponApplicable: previousValues[it.id]} : it
            )));
            toast.error(error.response?.data?.detail || 'Failed to update coupon setting');
        } finally {
            setCouponSavingIds((prev) => prev.filter((id) => !idSet.has(id)));
        }
    };

    const handleBulkCoupon = async (couponApplicable) => {
        await updateCouponApplicable(selectedItemIds, couponApplicable);
        setSelectedItemIds([]);
    };

    // In stock / out of stock for one or many items — optimistic, rolls back
    // only the affected items if the request fails.
    const isItemAvailable = (item) => item.isAvailable !== false;

    const updateAvailability = async (ids, isAvailable) => {
        if (!ids.length) return;
        const idSet = new Set(ids);
        const previousValues = Object.fromEntries(
            items.filter((it) => idSet.has(it.id)).map((it) => [it.id, it.isAvailable])
        );

        setAvailabilitySavingIds((prev) => [...new Set([...prev, ...ids])]);
        setItems((prev) => prev.map((it) => (idSet.has(it.id) ? {...it, isAvailable} : it)));

        try {
            await axios.patch(`${API}/items/availability`, {
                ids,
                isAvailable,
                restaurantId
            });
            toast.success(
                ids.length === 1
                    ? `Item marked ${isAvailable ? 'in stock' : 'out of stock'}`
                    : `${ids.length} items marked ${isAvailable ? 'in stock' : 'out of stock'}`
            );
        } catch (error) {
            console.error('Failed to update availability:', error);
            setItems((prev) => prev.map((it) => (
                idSet.has(it.id) ? {...it, isAvailable: previousValues[it.id]} : it
            )));
            toast.error(error.response?.data?.detail || 'Failed to update item status');
        } finally {
            setAvailabilitySavingIds((prev) => prev.filter((id) => !idSet.has(id)));
        }
    };

    // Item-level "Recommended" flag (optimistic). Only ever touches
    // isRecommended — turning it ON/OFF never changes stock status.
    const [recommendedSavingIds, setRecommendedSavingIds] = useState([]);
    const updateRecommended = async (ids, isRecommended) => {
        if (!ids.length) return;
        const idSet = new Set(ids);
        const previousValues = Object.fromEntries(
            items.filter((it) => idSet.has(it.id)).map((it) => [it.id, it.isRecommended])
        );
        setRecommendedSavingIds((prev) => [...new Set([...prev, ...ids])]);
        setItems((prev) => prev.map((it) => (idSet.has(it.id) ? {...it, isRecommended} : it)));
        try {
            await axios.patch(`${API}/items/recommended`, {ids, isRecommended, restaurantId});
            toast.success(
                ids.length === 1
                    ? `Item ${isRecommended ? 'added to' : 'removed from'} Recommended`
                    : `${ids.length} items ${isRecommended ? 'added to' : 'removed from'} Recommended`
            );
        } catch (error) {
            console.error('Failed to update recommended flag:', error);
            setItems((prev) => prev.map((it) => (
                idSet.has(it.id) ? {...it, isRecommended: previousValues[it.id]} : it
            )));
            toast.error(error.response?.data?.detail || 'Failed to update Recommended');
        } finally {
            setRecommendedSavingIds((prev) => prev.filter((id) => !idSet.has(id)));
        }
    };

    // "Without Onion & Garlic" flag for one or many items (optimistic).
    const [nogSaving, setNogSaving] = useState(false);
    const updateNoOnionGarlic = async (ids, noOnionGarlic) => {
        if (!ids.length) return;
        const idSet = new Set(ids);
        const previousValues = Object.fromEntries(
            items.filter((it) => idSet.has(it.id)).map((it) => [it.id, it.noOnionGarlic])
        );
        setNogSaving(true);
        setItems((prev) => prev.map((it) => (idSet.has(it.id) ? {...it, noOnionGarlic} : it)));
        try {
            await axios.patch(`${API}/items/no-onion-garlic`, {ids, noOnionGarlic, restaurantId});
            toast.success(`${ids.length} item${ids.length === 1 ? '' : 's'} ${noOnionGarlic ? 'marked' : 'unmarked'} as without onion & garlic`);
        } catch (error) {
            console.error('Failed to update onion/garlic flag:', error);
            setItems((prev) => prev.map((it) => (
                idSet.has(it.id) ? {...it, noOnionGarlic: previousValues[it.id]} : it
            )));
            toast.error(error.response?.data?.detail || 'Failed to update item');
        } finally {
            setNogSaving(false);
        }
    };

    const handleBulkNoOnionGarlic = async (value) => {
        await updateNoOnionGarlic(selectedItemIds, value);
        setSelectedItemIds([]);
    };

    const openBulkItemCommission = () => {
        setItemCommissionForm({type: 'PERCENTAGE', value: ''});
        setItemCommissionOpen(true);
    };

    const saveBulkItemCommission = async () => {
        const {type, value} = itemCommissionForm;
        const err = commissionValueError(type, value);
        if (err) return toast.error(err);
        if (!selectedItemIds.length) return toast.error('Select at least one item');
        setItemCommissionSaving(true);
        try {
            const res = await axios.patch(`${API}/items/commission`, {
                ids: selectedItemIds,
                restaurantId,
                commissionType: type === 'DEFAULT' ? null : type,
                commissionValue: type === 'DEFAULT' ? null : Number(value)
            });
            const c = res.data || {};
            const ids = new Set(selectedItemIds.map(String));
            setItems((prev) => prev.map((it) => (ids.has(String(it.id))
                ? {...it, commissionType: c.commissionType ?? null, commissionValue: c.commissionValue ?? null}
                : it)));
            toast.success(c.message || 'Item commission updated');
            setItemCommissionOpen(false);
            setSelectedItemIds([]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update item commission');
        } finally {
            setItemCommissionSaving(false);
        }
    };

    // Mark EVERY item of one category in/out of stock in a single request.
    // Applies to the whole category (not just what the search box is showing)
    // and only sends items that actually need to change.
    const handleCategoryAvailability = async (sectionKey, sectionTitle, isAvailable) => {
        if (availabilitySavingIds.length > 0) return;
        const inCategory = items.filter((it) => bucketKey(it) === sectionKey);
        const toChange = inCategory.filter((it) => isItemAvailable(it) !== isAvailable);
        if (toChange.length === 0) {
            toast.info(`All ${inCategory.length} item(s) in "${sectionTitle}" are already ${isAvailable ? 'in stock' : 'out of stock'}`);
            return;
        }
        const ok = window.confirm(
            `Mark ${toChange.length} item(s) in "${sectionTitle}" as ${isAvailable ? 'IN STOCK' : 'OUT OF STOCK'}?`
        );
        if (!ok) return;
        await updateAvailability(toChange.map((it) => it.id), isAvailable);
    };

    const handleBulkAvailability = async (isAvailable) => {
        await updateAvailability(selectedItemIds, isAvailable);
        setSelectedItemIds([]);
    };

    const handleVariantSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...variantForm,
                resturantId: restaurantId
            };

            await axios.post(`${API}/items/${selectedItem.id}/variants`, data);
            toast.success('Variant added successfully');
            setShowVariantDialog(false);
            fetchItemDetails(selectedItem.id);
            setVariantForm({title: '', foodType: 'VEG', price: '', mrpPrice: '', isDefault: false});
        } catch (error) {
            console.error('Failed to add variant:', error);
            toast.error('Failed to add variant');
        }
    };

    const deleteVariant = async (variantId) => {
        if (!window.confirm('Delete this variant?')) return;

        try {
            await axios.delete(`${API}/items/variants/${variantId}`);
            toast.success('Variant deleted successfully');
            fetchItemDetails(selectedItem.id);
        } catch (error) {
            console.error('Failed to delete variant:', error);
            toast.error('Failed to delete variant');
        }
    };

    const handleAddonSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...addonForm,
                resturantId: restaurantId
            };

            await axios.post(`${API}/items/${selectedItem.id}/addons`, data);
            toast.success('Addon added successfully');
            setShowAddonDialog(false);
            fetchItemDetails(selectedItem.id);
            setAddonForm({title: '', foodType: 'VEG', price: '', mrpPrice: ''});
        } catch (error) {
            console.error('Failed to add addon:', error);
            toast.error('Failed to add addon');
        }
    };

    const deleteAddon = async (addonId) => {
        if (!window.confirm('Delete this addon?')) return;

        try {
            await axios.delete(`${API}/items/addons/${addonId}`);
            toast.success('Addon deleted successfully');
            fetchItemDetails(selectedItem.id);
        } catch (error) {
            console.error('Failed to delete addon:', error);
            toast.error('Failed to delete addon');
        }
    };

    if (loading) {
        return <div className="flex justify-center p-8">Loading...</div>;
    }

    // Map of known category IDs -> name, for THIS restaurant only.
    const categoryNameById = categories.reduce((acc, c) => {
        acc[String(c.id)] = c.name;
        return acc;
    }, {});

    // An item's categoryId only counts as a real category if it matches one
    // of this restaurant's known categories. Anything else (blank, or an ID
    // that doesn't belong to this restaurant — e.g. leftover demo/template
    // IDs from a bulk import) falls back to "Uncategorized" so it still shows
    // up instead of silently disappearing.
    const bucketKey = (it) => {
        const cid = it.categoryId ? String(it.categoryId) : '';
        return cid && categoryNameById[cid] ? cid : 'uncategorized';
    };

    // Filter items by selected category chip + search query
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const couponBlockedCount = items.filter((it) => it.couponApplicable === false).length;
    const filteredItems = items.filter((it) => {
        // "Coupon blocked only" filter
        if (couponBlockedOnly && it.couponApplicable !== false) return false;
        // Category chip filter
        if (categoryFilter === 'uncategorized') {
            if (bucketKey(it) !== 'uncategorized') return false;
        } else if (categoryFilter !== 'all') {
            if (bucketKey(it) !== String(categoryFilter)) return false;
        }
        // Search filter — name, description, cuisines
        if (normalizedQuery) {
            const name = (it.name || '').toLowerCase();
            const desc = (it.description || '').toLowerCase();
            const cuisines = Array.isArray(it.cuisines)
                ? it.cuisines.join(' ').toLowerCase()
                : String(it.cuisines || '').toLowerCase();
            if (
                !name.includes(normalizedQuery) &&
                !desc.includes(normalizedQuery) &&
                !cuisines.includes(normalizedQuery)
            ) {
                return false;
            }
        }
        return true;
    });

    // Count items per category for chip badges (uses ALL items, not filtered)
    const counts = items.reduce(
        (acc, it) => {
            const k = bucketKey(it);
            acc[k] = (acc[k] || 0) + 1;
            return acc;
        },
        {uncategorized: 0}
    );

    // Group filtered items by category for sectioned rendering.
    // Sections are ordered to match the `categories` list, then "Uncategorized" last.
    const groupedItems = filteredItems.reduce((acc, it) => {
        const key = bucketKey(it);
        if (!acc[key]) acc[key] = [];
        acc[key].push(it);
        return acc;
    }, {});
    // Items per category AND per sub-category (for the commission dialog).
    const commissionItemCounts = items.reduce((acc, it) => {
        if (it.categoryId) acc[String(it.categoryId)] = (acc[String(it.categoryId)] || 0) + 1;
        if (it.subcategoryId) acc[String(it.subcategoryId)] = (acc[String(it.subcategoryId)] || 0) + 1;
        return acc;
    }, {});

    const sectionOrder = [
        ...categories.map((c) => String(c.id)).filter((id) => groupedItems[id]),
        ...(groupedItems.uncategorized ? ['uncategorized'] : [])
    ];

    // ---------------------------------------------------------------------
    // 📤 Export — the items currently shown (category chip / search /
    // coupon filter applied) as .xlsx or .csv. Items with variants get one
    // row per variant so every sellable price is in the sheet.
    // ---------------------------------------------------------------------
    const money = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && v !== null && v !== '' ? Math.round(n * 100) / 100 : '';
    };
    const yesNo = (v) => (v ? 'Yes' : 'No');
    const foodTypeLabel = (t) => {
        const k = String(t || '').toUpperCase().replace(/[^A-Z]/g, '');
        if (k === 'VEG') return 'Veg';
        if (k === 'NONVEG') return 'Non-Veg';
        if (k === 'EGG') return 'Egg';
        return t || '';
    };
    const discountCols = (price, mrp) => {
        const p = Number(price);
        const m = Number(mrp);
        if (!(m > 0) || !(p >= 0) || m <= p) {
            return {'Discount (₹)': 0, 'Discount (%)': 0};
        }
        return {
            'Discount (₹)': money(m - p),
            'Discount (%)': Math.round(((m - p) / m) * 1000) / 10,
        };
    };

    const handleExport = async (format) => {
        const list = filteredItems;
        if (!list.length) {
            toast.error('No items to export');
            return;
        }
        setExporting(true);
        try {
            // All variants for this restaurant in one request.
            let variantsByItem = {};
            if (list.some((it) => it.hasVariants)) {
                try {
                    const res = await axios.get(`${API}/items/restaurant/${restaurantId}/variants`);
                    const variants = Array.isArray(res.data) ? res.data : [];
                    variantsByItem = variants.reduce((acc, v) => {
                        const k = String(v.itemId);
                        (acc[k] = acc[k] || []).push(v);
                        return acc;
                    }, {});
                } catch (err) {
                    console.error('Variant fetch for export failed:', err);
                    toast.warning('Variants could not be loaded — exporting base prices only');
                }
            }

            const rows = [];
            list.forEach((it) => {
                const catKey = bucketKey(it);
                const base = {
                    'Item ID': it.id,
                    'Item Name': it.name || '',
                    'Category': catKey === 'uncategorized' ? 'Uncategorized' : (categoryNameById[catKey] || ''),
                    'Sub Category': it.subcategoryId ? (categoryNameById[String(it.subcategoryId)] || '') : '',
                    'Food Type': foodTypeLabel(it.foodType),
                };
                const flags = {
                    'Available': yesNo(it.isAvailable !== false),
                    'Recommended': yesNo(it.isRecommended),
                    'Coupon Applicable': yesNo(it.couponApplicable !== false),
                    'Without Onion & Garlic': yesNo(it.noOnionGarlic === true),
                    'Category Offer': yesNo(it.catOffer),
                    '₹99 Store': yesNo(it.storenintynine),
                    '₹250 Store': yesNo(it.storetwofifty),
                    'Tag': it.tag || '',
                    'Priority': Number(it.priority || 0),
                    'Cuisines': Array.isArray(it.cuisines) ? it.cuisines.join(', ') : (it.cuisines || ''),
                    'Description': it.description || '',
                    'Image URL': it.image || '',
                };

                const variants = it.hasVariants ? (variantsByItem[String(it.id)] || []) : [];
                if (variants.length) {
                    variants.forEach((v) => {
                        rows.push({
                            ...base,
                            'Food Type': foodTypeLabel(v.foodType || it.foodType),
                            'Variant': `${v.title || ''}${v.isDefault ? ' (default)' : ''}`,
                            'Restaurant Price (₹)': money(v.price),
                            'MRP (₹)': money(v.mrpPrice),
                            'Commission': `${commissionLabel(commissionFor(it))} (${commissionFor(it).source})`,
                            'Customer Price (₹)': money(customerPrice(v.price, commissionFor(it))),
                            ...discountCols(v.price, v.mrpPrice),
                            ...flags,
                        });
                    });
                } else {
                    rows.push({
                        ...base,
                        'Variant': '',
                        'Restaurant Price (₹)': money(it.price),
                        'MRP (₹)': money(it.mrpPrice),
                        'Commission': `${commissionLabel(commissionFor(it))} (${commissionFor(it).source})`,
                        'Customer Price (₹)': money(customerPrice(it.price, commissionFor(it))),
                        ...discountCols(it.price, it.mrpPrice),
                        ...flags,
                    });
                }
            });

            const name = `menu-items-${String(restaurantId || '').slice(-6)}`;
            const ok = format === 'csv'
                ? exportToCsv(rows, name)
                : exportToExcel(rows, name, 'Menu Items',
                    [26, 32, 20, 18, 10, 18, 10, 10, 12, 10, 10, 10, 12, 18, 14, 12, 12, 16, 8, 20, 40, 40]);
            if (ok) {
                toast.success(`Exported ${list.length} item${list.length === 1 ? '' : 's'} (${rows.length} rows) as ${format === 'csv' ? 'CSV' : 'Excel'}`);
            }
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold">
                    Menu Items{' '}
                    <span className="text-gray-500 font-normal text-lg">
            ({filteredItems.length}
                        {filteredItems.length !== items.length ? ` of ${items.length}` : ''})
          </span>
                </h2>

                <div className="flex gap-2">
                    {/* 📤 Export (current filter) */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                disabled={exporting || loading || items.length === 0}
                                data-testid="export-items-button"
                            >
                                {exporting
                                    ? <Loader2 size={18} className="mr-2 animate-spin"/>
                                    : <Download size={18} className="mr-2"/>}
                                {exporting ? 'Exporting…' : 'Export'}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel className="text-xs font-normal text-gray-500">
                                {filteredItems.length === items.length
                                    ? `All ${items.length} items`
                                    : `${filteredItems.length} filtered items`}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                                <FileSpreadsheet size={16} className="mr-2 text-green-600"/>
                                Excel (.xlsx)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleExport('csv')}>
                                <FileText size={16} className="mr-2 text-blue-600"/>
                                CSV (.csv)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Category-wise commission */}
                    <Button
                        variant="outline"
                        onClick={() => setCommissionDialog('all')}
                        data-testid="category-commission-button"
                    >
                        <BadgePercent size={18} className="mr-2 text-orange-600"/>
                        Category commission
                    </Button>

                    {/* 🔥 Bulk Upload */}
                    <Button
                        onClick={() => setOpenBulk(true)}
                        className="bg-gray-800 text-white"
                    >
                        Bulk Upload
                    </Button>

                    {/* Existing */}
                    <Button
                        onClick={openCreateItemDialog}
                        style={{backgroundColor: "#C2410C"}}
                    >
                        <Plus size={20} className="mr-2"/>
                        Add Item
                    </Button>
                </div>
            </div>

            {openBulk && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl w-[400px] p-6">

                        <h3 className="text-lg font-semibold mb-4">
                            Bulk Upload Items
                        </h3>

                        {/* 📥 Download Sample */}
                        <a
                            href="/restaurant_bulk_import_template.xlsx"
                            download
                            className="text-blue-600 underline text-sm mb-4 block"
                        >
                            Download Sample File
                        </a>

                        {/* 📤 File Upload */}
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => setFile(e.target.files[0])}
                            className="mb-4"
                        />

                        {/* Buttons */}
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setOpenBulk(false)}
                                className="px-4 py-2 border rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleUpload}
                                className="px-4 py-2 bg-orange-600 text-white rounded"
                            >
                                Upload
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Search Bar */}
            <div className="relative w-full sm:max-w-md">
                <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                    type="text"
                    placeholder="Search menu items by name, description, cuisine..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                    data-testid="items-search-input"
                />
                {searchQuery && (
                    <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                        data-testid="items-search-clear"
                        aria-label="Clear search"
                    >
                        <X size={14}/>
                    </button>
                )}
            </div>

            {/* Category Filter Chips */}
            {categories.length > 0 && (
                <div
                    className="flex flex-wrap gap-2"
                    data-testid="items-category-filter-bar"
                >
                    <button
                        type="button"
                        data-testid="items-cat-chip-all"
                        onClick={() => setCategoryFilter('all')}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            categoryFilter === 'all'
                                ? 'bg-orange-600 text-white border-orange-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                        }`}
                    >
                        All <span className="opacity-75">({items.length})</span>
                    </button>

                    {categories.map((c) => {
                        const n = counts[c.id] || 0;
                        const active = categoryFilter === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`items-cat-chip-${c.id}`}
                                onClick={() => setCategoryFilter(c.id)}
                                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                    active
                                        ? 'bg-orange-600 text-white border-orange-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                                }`}
                            >
                                {c.name}{' '}
                                <span className={active ? 'opacity-90' : 'text-gray-500'}>
                  ({n})
                </span>
                            </button>
                        );
                    })}

                    {counts.uncategorized > 0 && (
                        <button
                            type="button"
                            data-testid="items-cat-chip-uncategorized"
                            onClick={() => setCategoryFilter('uncategorized')}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                categoryFilter === 'uncategorized'
                                    ? 'bg-orange-600 text-white border-orange-600'
                                    : 'bg-white text-gray-500 border-gray-300 hover:border-orange-400'
                            }`}
                        >
                            Uncategorized{' '}
                            <span className="opacity-75">({counts.uncategorized})</span>
                        </button>
                    )}
                </div>
            )}

            {/* Select all / bulk delete bar */}
            {items.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="items-select-all"
                            checked={selectedItemIds.length === items.length}
                            onChange={toggleSelectAllItems}
                        />
                        Select all ({items.length})
                    </label>

                    <label
                        className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
                        title="Show only items where coupons can't be applied"
                    >
                        <input
                            type="checkbox"
                            data-testid="items-coupon-blocked-filter"
                            checked={couponBlockedOnly}
                            onChange={(e) => setCouponBlockedOnly(e.target.checked)}
                        />
                        Coupon blocked only ({couponBlockedCount})
                    </label>

                    {selectedItemIds.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 ml-auto">
                            <span className="text-sm text-gray-600">{selectedItemIds.length} selected</span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={availabilitySavingIds.length > 0}
                                data-testid="items-mark-in-stock"
                                onClick={() => handleBulkAvailability(true)}
                            >
                                <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-green-600"/>
                                Mark In Stock
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={availabilitySavingIds.length > 0}
                                data-testid="items-mark-out-of-stock"
                                onClick={() => handleBulkAvailability(false)}
                            >
                                <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-gray-400"/>
                                Mark Out of Stock
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={nogSaving}
                                data-testid="items-mark-no-onion-garlic"
                                onClick={() => handleBulkNoOnionGarlic(true)}
                            >
                                🧅 Mark No Onion/Garlic
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={nogSaving}
                                data-testid="items-unmark-no-onion-garlic"
                                onClick={() => handleBulkNoOnionGarlic(false)}
                            >
                                Unmark No Onion/Garlic
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={couponSavingIds.length > 0}
                                data-testid="items-enable-coupons"
                                onClick={() => handleBulkCoupon(true)}
                            >
                                <TicketPercent size={14} className="mr-1 text-green-700" />
                                Enable Coupons
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={couponSavingIds.length > 0}
                                data-testid="items-disable-coupons"
                                onClick={() => handleBulkCoupon(false)}
                            >
                                <TicketX size={14} className="mr-1 text-red-600" />
                                Disable Coupons
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={itemCommissionSaving}
                                data-testid="items-set-commission"
                                onClick={openBulkItemCommission}
                            >
                                <BadgePercent size={14} className="mr-1 text-orange-600"/>
                                Set Commission
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={bulkDeleting}
                                data-testid="items-delete-selected"
                                onClick={handleBulkDeleteItems}
                            >
                                <Trash2 size={14} className="mr-1" />
                                {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Items grouped by category */}
            <div className="space-y-8">
                {sectionOrder.map((sectionKey) => {
                    const sectionTitle =
                        sectionKey === 'uncategorized'
                            ? 'Uncategorized'
                            : (categoryNameById[sectionKey] || 'Category');
                    const sectionItems = groupedItems[sectionKey] || [];
                    if (sectionItems.length === 0) return null;
                    return (
                        <section
                            key={sectionKey}
                            data-testid={`items-section-${sectionKey}`}
                        >
                            <div className="flex items-baseline gap-3 mb-3 pb-2 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {sectionTitle}
                                </h3>
                                <span className="text-sm text-gray-500">
                                    {sectionItems.length} item{sectionItems.length === 1 ? '' : 's'}
                                </span>
                                {canEditItems && (() => {
                                    const inStockCount = sectionItems.length - sectionItems.filter((it) => !isItemAvailable(it)).length;
                                    const busy = availabilitySavingIds.length > 0;
                                    return (
                                        <div className="ml-auto flex items-center gap-2" data-testid={`section-stock-actions-${sectionKey}`}>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                title={`Mark every item in ${sectionTitle} out of stock`}
                                                data-testid={`section-out-of-stock-${sectionKey}`}
                                                onClick={() => handleCategoryAvailability(sectionKey, sectionTitle, false)}
                                            >
                                                <PackageX size={13}/>
                                                All out of stock
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                className="inline-flex items-center gap-1 rounded-full border border-green-200 px-2.5 py-0.5 text-xs text-green-700 hover:bg-green-50 disabled:opacity-50"
                                                title={`Mark every item in ${sectionTitle} in stock (${inStockCount}/${sectionItems.length} in stock now)`}
                                                data-testid={`section-in-stock-${sectionKey}`}
                                                onClick={() => handleCategoryAvailability(sectionKey, sectionTitle, true)}
                                            >
                                                <PackageCheck size={13}/>
                                                All in stock
                                            </button>
                                        </div>
                                    );
                                })()}
                                {sectionKey !== 'uncategorized' && (() => {
                                    const rule = commissionRules.get(sectionKey);
                                    return (
                                        <button
                                            type="button"
                                            className={`${canEditItems ? '' : 'ml-auto'} inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-gray-700 hover:border-orange-400 hover:text-orange-700`}
                                            title="Set commission for this category"
                                            data-testid={`section-commission-${sectionKey}`}
                                            onClick={() => setCommissionDialog(sectionKey)}
                                        >
                                            <BadgePercent size={13} className="text-orange-600"/>
                                            {rule
                                                ? `Commission: ${commissionLabel(rule)}`
                                                : `Commission: restaurant default (${commissionLabel(restaurantCommission)})`}
                                        </button>
                                    );
                                })()}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {sectionItems.map((item) => (
                                    <Card
                                        key={item.id}
                                        className={`hover:shadow-lg transition-shadow cursor-pointer ${isItemAvailable(item) ? '' : 'bg-gray-50'}`}
                                        onClick={() => fetchItemDetails(item.id)}
                                    >
                                        <CardHeader className="pb-3">
                                            <input
                                                type="checkbox"
                                                className="mb-2"
                                                data-testid={`item-select-${item.id}`}
                                                checked={selectedItemIds.includes(item.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => toggleSelectItem(item.id)}
                                            />
                                            {item.image && (
                                                <img src={item.image} alt={item.name}
                                                     className={`w-full h-32 object-cover rounded-lg mb-3 ${isItemAvailable(item) ? '' : 'grayscale opacity-60'}`}/>
                                            )}
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <CardTitle className="text-lg">{item.name}</CardTitle>
                                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                                        <Badge
                                                            className={item.foodType === 'VEG' ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                                                            {item.foodType || 'VEG'}
                                                        </Badge>
                                                        {!item.isAvailable && (
                                                            <Badge
                                                                className="bg-gray-100 text-gray-800 border-0">Out of
                                                                Stock</Badge>
                                                        )}
                                                        {item.tag && (
                                                            <Badge
                                                                className={`${ITEM_TAG_STYLES[item.tag] || 'bg-gray-100 text-gray-800'} border-0`}
                                                                data-testid={`item-tag-${item.id}`}
                                                            >
                                                                {item.tag}
                                                            </Badge>
                                                        )}
                                                        <Badge
                                                            variant="outline"
                                                            className="text-gray-600"
                                                            title="Display order (lower shows first in the app)"
                                                            data-testid={`item-priority-${item.id}`}
                                                        >
                                                            Priority: {item.priority ?? 0}
                                                        </Badge>
                                                        {item.noOnionGarlic === true && (
                                                            <Badge
                                                                className="bg-lime-100 text-lime-800 border-0"
                                                                data-testid={`item-no-onion-garlic-${item.id}`}
                                                            >
                                                                No Onion/Garlic
                                                            </Badge>
                                                        )}
                                                        {!isCouponApplicable(item) && (
                                                            <Badge
                                                                className="bg-amber-100 text-amber-800 border-0"
                                                                data-testid={`item-coupon-blocked-${item.id}`}
                                                            >
                                                                No Coupons
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        disabled={couponSavingIds.includes(item.id)}
                                                        data-testid={`item-coupon-toggle-${item.id}`}
                                                        title={isCouponApplicable(item)
                                                            ? 'Coupons apply to this item — click to disable'
                                                            : 'Coupons blocked on this item — click to enable'}
                                                        aria-label={isCouponApplicable(item) ? 'Disable coupons' : 'Enable coupons'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            updateCouponApplicable([item.id], !isCouponApplicable(item));
                                                        }}
                                                    >
                                                        {isCouponApplicable(item)
                                                            ? <TicketPercent size={16} className="text-green-700"/>
                                                            : <TicketX size={16} className="text-red-600"/>}
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditItemDialog(item);
                                                    }}>
                                                        <Edit size={16}/>
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteItem(item.id);
                                                    }}>
                                                        <Trash2 size={16} className="text-red-600"/>
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                                            <div className="flex justify-between items-center">
                                                {(() => {
                                                    const c = commissionFor(item);
                                                    const cp = customerPrice(item.price, c);
                                                    return (
                                                        <div className="space-y-0.5">
                                                            <div className="text-xs text-gray-500">Restaurant price</div>
                                                            <div>
                                                                <span className="text-lg font-bold" data-testid={`item-restaurant-price-${item.id}`}>{formatRupee(item.price)}</span>
                                                                {item.mrpPrice ? (
                                                                    <span className="text-sm text-gray-500 line-through ml-2">{formatRupee(item.mrpPrice)}</span>
                                                                ) : null}
                                                            </div>
                                                            {c.own && (
                                                                <Badge className="border-0 bg-orange-50 text-orange-700 text-[11px] px-1.5 py-0">
                                                                    Item commission: {commissionLabel(c)}
                                                                </Badge>
                                                            )}
                                                            <div className="text-xs text-gray-600" title={c.source}>
                                                                Customer price <span className="font-semibold text-gray-900">{formatRupee(cp)}</span>
                                                                <span className="text-gray-400"> · {commissionLabel(c)} ({c.source})</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                {item.hasVariants && (
                                                    <Badge variant="outline">Has Variants</Badge>
                                                )}
                                            </div>

                                            {/* In stock / out of stock toggle */}
                                            <div
                                                className="mt-3 pt-3 border-t flex items-center justify-between"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span
                                                    className={`text-sm font-medium ${isItemAvailable(item) ? 'text-green-700' : 'text-gray-500'}`}
                                                >
                                                    {availabilitySavingIds.includes(item.id)
                                                        ? 'Saving…'
                                                        : isItemAvailable(item) ? 'In Stock' : 'Out of Stock'}
                                                </span>
                                                <Switch
                                                    checked={isItemAvailable(item)}
                                                    disabled={availabilitySavingIds.includes(item.id)}
                                                    onCheckedChange={(checked) => updateAvailability([item.id], checked)}
                                                    aria-label={isItemAvailable(item) ? 'Mark out of stock' : 'Mark in stock'}
                                                    data-testid={`item-availability-toggle-${item.id}`}
                                                />
                                            </div>

                                            {/* Recommended toggle — independent of stock status */}
                                            <div
                                                className="mt-2 flex items-center justify-between"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span
                                                    className={`text-sm font-medium ${item.isRecommended === true ? 'text-amber-700' : 'text-gray-500'}`}
                                                >
                                                    {recommendedSavingIds.includes(item.id)
                                                        ? 'Saving…'
                                                        : item.isRecommended === true ? 'Recommended' : 'Not Recommended'}
                                                </span>
                                                <Switch
                                                    checked={item.isRecommended === true}
                                                    disabled={recommendedSavingIds.includes(item.id)}
                                                    onCheckedChange={(checked) => updateRecommended([item.id], checked)}
                                                    aria-label={item.isRecommended === true ? 'Remove from Recommended' : 'Add to Recommended'}
                                                    data-testid={`item-recommended-toggle-${item.id}`}
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>

            {filteredItems.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    {items.length === 0
                        ? 'No items found. Add your first menu item!'
                        : searchQuery
                            ? `No items match "${searchQuery}".`
                            : 'No items in this category.'}
                </div>
            )}

            {/* Item Dialog */}
            {/* Bulk item-wise commission */}
            <Dialog open={itemCommissionOpen} onOpenChange={(o) => !itemCommissionSaving && setItemCommissionOpen(o)}>
                <DialogContent className="max-w-md" data-testid="bulk-item-commission-dialog">
                    <DialogHeader>
                        <DialogTitle>Item commission — {selectedItemIds.length} item{selectedItemIds.length === 1 ? '' : 's'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                {v: 'PERCENTAGE', label: 'Percentage (%)'},
                                {v: 'FLAT', label: 'Flat (₹)'},
                                {v: 'DEFAULT', label: 'Category / restaurant'}
                            ].map((o) => (
                                <button
                                    type="button"
                                    key={o.v}
                                    onClick={() => setItemCommissionForm((f) => ({type: o.v, value: o.v === 'DEFAULT' ? '' : f.value}))}
                                    className={`rounded-md border-2 px-2 py-2 text-xs font-medium transition-colors ${
                                        itemCommissionForm.type === o.v
                                            ? 'border-orange-500 bg-orange-50 text-orange-800'
                                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                    }`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                        {itemCommissionForm.type === 'DEFAULT' ? (
                            <p className="text-sm text-gray-600">
                                Removes the item commission — these items will use their category rule, or the restaurant
                                commission ({commissionLabel(restaurantCommission)}).
                            </p>
                        ) : (() => {
                            const err = commissionValueError(itemCommissionForm.type, itemCommissionForm.value);
                            return (
                                <div>
                                    <Label>Value</Label>
                                    <div className="relative mt-1.5">
                                        {itemCommissionForm.type === 'FLAT' && (
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                                        )}
                                        <Input
                                            type="number" min="0" step="0.01" autoFocus
                                            className={`${itemCommissionForm.type === 'FLAT' ? 'pl-7' : ''} pr-8 ${err && itemCommissionForm.value !== '' ? 'border-red-500' : ''}`}
                                            value={itemCommissionForm.value}
                                            onChange={(e) => setItemCommissionForm((f) => ({...f, value: e.target.value}))}
                                        />
                                        {itemCommissionForm.type === 'PERCENTAGE' && (
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                                        )}
                                    </div>
                                    {err && itemCommissionForm.value !== ''
                                        ? <p className="text-xs text-red-600 mt-1">{err}</p>
                                        : <p className="text-xs text-gray-500 mt-1">Overrides the category and restaurant commission for the selected items.</p>}
                                </div>
                            );
                        })()}
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" disabled={itemCommissionSaving}
                                    onClick={() => setItemCommissionOpen(false)}>Cancel</Button>
                            <Button
                                type="button"
                                style={{backgroundColor: '#C2410C'}}
                                disabled={itemCommissionSaving || !!commissionValueError(itemCommissionForm.type, itemCommissionForm.value)}
                                onClick={saveBulkItemCommission}
                                data-testid="bulk-item-commission-save"
                            >
                                {itemCommissionSaving && <Loader2 size={16} className="mr-2 animate-spin"/>}
                                Save
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <CategoryCommissionDialog
                open={!!commissionDialog}
                onOpenChange={(o) => !o && setCommissionDialog(null)}
                restaurantId={restaurantId}
                restaurantCommission={restaurantCommission}
                itemCounts={commissionItemCounts}
                focusCategoryId={commissionDialog && commissionDialog !== 'all' ? commissionDialog : null}
                onSaved={fetchCommission}
            />

            <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{currentItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleItemSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label>Item Name *</Label>
                                <Input value={itemForm.name}
                                       onChange={(e) => setItemForm({...itemForm, name: e.target.value})} required/>
                            </div>

                            <div className="col-span-2">
                                <Label>Description</Label>
                                <Textarea value={itemForm.description}
                                          onChange={(e) => setItemForm({...itemForm, description: e.target.value})}
                                          rows={2}/>
                            </div>

                            <div>
                                <Label>Food Type</Label>
                                <Select value={itemForm.foodType}
                                        onValueChange={(value) => setItemForm({...itemForm, foodType: value})}>
                                    <SelectTrigger>
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="VEG">Veg</SelectItem>
                                        <SelectItem value="NONVEG">Non-Veg</SelectItem>
                                        <SelectItem value="EGG">Egg</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>Category</Label>
                                <Select
                                    value={itemForm.categoryId || 'none'}
                                    onValueChange={(value) => {
                                        const newCategoryId = value === 'none' ? '' : value;
                                        setItemForm({
                                            ...itemForm,
                                            categoryId: newCategoryId
                                        });
                                    }}
                                >
                                    <SelectTrigger data-testid="item-category-trigger">
                                        <SelectValue
                                            placeholder={
                                                categories.length === 0
                                                    ? 'No categories available'
                                                    : 'Select category'
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No category</SelectItem>
                                        {categories.map((c) => (
                                            <SelectItem
                                                key={c.id}
                                                value={c.id}
                                                data-testid={`item-category-option-${c.id}`}
                                            >
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {categories.length === 0 && (
                                    <p className="text-xs text-amber-600 mt-1">
                                        Add categories for this restaurant first (Categories →
                                        Restaurant Categories).
                                    </p>
                                )}
                            </div>

                            <div>
                                <Label>Cuisines (comma separated)</Label>
                                <Input value={itemForm.cuisines}
                                       onChange={(e) => setItemForm({...itemForm, cuisines: e.target.value})}
                                       placeholder="Italian, Chinese"/>
                            </div>

                            <div>
                                <Label>Restaurant Price *</Label>
                                <Input type="number" step="0.01" value={itemForm.price}
                                       onChange={(e) => setItemForm({...itemForm, price: e.target.value})} required/>
                            </div>

                            <div>
                                <Label>MRP Price</Label>
                                <Input type="number" step="0.01" value={itemForm.mrpPrice}
                                       onChange={(e) => setItemForm({...itemForm, mrpPrice: e.target.value})}/>
                            </div>

                            {/* Item-wise commission */}
                            <div className="col-span-2 rounded-lg border p-3 space-y-2" data-testid="item-commission-section">
                                <div className="flex items-center justify-between">
                                    <Label className="flex items-center gap-1.5">
                                        <BadgePercent size={15} className="text-orange-600"/> Item commission
                                    </Label>
                                    {(() => {
                                        const inherited = commissionFor({
                                            categoryId: itemForm.categoryId || null,
                                            subcategoryId: currentItem?.subcategoryId || null
                                        });
                                        return itemForm.commissionType === 'DEFAULT' ? (
                                            <span className="text-xs text-gray-500">
                                                Uses {inherited.source.toLowerCase()}: {commissionLabel(inherited)}
                                            </span>
                                        ) : null;
                                    })()}
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        {v: 'DEFAULT', label: 'Category / restaurant'},
                                        {v: 'PERCENTAGE', label: 'Percentage (%)'},
                                        {v: 'FLAT', label: 'Flat (₹)'}
                                    ].map((o) => (
                                        <button
                                            type="button"
                                            key={o.v}
                                            data-testid={`item-commission-type-${o.v}`}
                                            onClick={() => setItemForm({
                                                ...itemForm,
                                                commissionType: o.v,
                                                commissionValue: o.v === 'DEFAULT' ? '' : itemForm.commissionValue
                                            })}
                                            className={`rounded-md border-2 px-2 py-1.5 text-xs font-medium transition-colors ${
                                                itemForm.commissionType === o.v
                                                    ? 'border-orange-500 bg-orange-50 text-orange-800'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                                {itemForm.commissionType !== 'DEFAULT' && (() => {
                                    const err = commissionValueError(itemForm.commissionType, itemForm.commissionValue);
                                    const preview = !err && itemForm.price !== ''
                                        ? Number(itemForm.price || 0) + commissionAmount(itemForm.price, itemForm.commissionType, itemForm.commissionValue)
                                        : null;
                                    return (
                                        <div>
                                            <div className="relative">
                                                {itemForm.commissionType === 'FLAT' && (
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                                                )}
                                                <Input
                                                    type="number" min="0" step="0.01"
                                                    max={itemForm.commissionType === 'PERCENTAGE' ? 100 : 10000}
                                                    className={`${itemForm.commissionType === 'FLAT' ? 'pl-7' : ''} pr-8 ${err ? 'border-red-500' : ''}`}
                                                    placeholder={itemForm.commissionType === 'PERCENTAGE' ? 'e.g. 15' : 'e.g. 20'}
                                                    value={itemForm.commissionValue}
                                                    data-testid="item-commission-value"
                                                    onChange={(e) => setItemForm({...itemForm, commissionValue: e.target.value})}
                                                />
                                                {itemForm.commissionType === 'PERCENTAGE' && (
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                                                )}
                                            </div>
                                            {err
                                                ? <p className="text-xs text-red-600 mt-1">{err}</p>
                                                : <p className="text-xs text-gray-500 mt-1">
                                                    Overrides the category and restaurant commission for this item (variants and add-ons too).
                                                    {preview !== null && <> Customer price: <b>{formatRupee(preview)}</b></>}
                                                </p>}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div>
                                <Label>Priority</Label>
                                <Input type="number" value={itemForm.priority}
                                       onChange={(e) => setItemForm({...itemForm, priority: e.target.value})}/>
                            </div>

                            <div>
                                <Label>Tag</Label>
                                <Select value={itemForm.tag || 'none'}
                                        onValueChange={(value) => setItemForm({...itemForm, tag: value})}>
                                    <SelectTrigger data-testid="item-tag-trigger">
                                        <SelectValue placeholder="No tag"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No tag</SelectItem>
                                        {ITEM_TAGS.map((t) => (
                                            <SelectItem key={t} value={t}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="col-span-2">
                                <Label>Item Image</Label>
                                <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])}/>
                            </div>

                            {/* Item-wise cart suggestions: shown in the customer's cart
                                when THIS item is in it. Multiple items can be picked. */}
                            <div className="col-span-2" data-testid="item-form-linked-recommended">
                                <Label>
                                    Suggest these items when this item is in the cart
                                    {' '}({(itemForm.recommendedItemIds || []).length} selected)
                                </Label>
                                <Input
                                    className="mt-1 mb-2"
                                    placeholder="Search items..."
                                    value={linkedSearch}
                                    onChange={(e) => setLinkedSearch(e.target.value)}
                                    data-testid="item-form-linked-search"
                                />
                                <div className="max-h-44 overflow-y-auto rounded-md border p-2 space-y-1">
                                    {items
                                        .filter((it) => String(it.id) !== String(currentItem?.id))
                                        .filter((it) => (it.name || '').toLowerCase().includes(linkedSearch.trim().toLowerCase()))
                                        .map((it) => (
                                            <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`item-form-linked-${it.id}`}
                                                    checked={(itemForm.recommendedItemIds || []).includes(String(it.id))}
                                                    onChange={(e) => setItemForm((f) => {
                                                        const cur = f.recommendedItemIds || [];
                                                        const id = String(it.id);
                                                        return {
                                                            ...f,
                                                            recommendedItemIds: e.target.checked
                                                                ? [...new Set([...cur, id])]
                                                                : cur.filter((x) => x !== id)
                                                        };
                                                    })}
                                                />
                                                <span>{it.name}</span>
                                                {it.isAvailable === false && (
                                                    <span className="text-xs text-gray-400">(unavailable)</span>
                                                )}
                                            </label>
                                        ))}
                                    {items.filter((it) => String(it.id) !== String(currentItem?.id)).length === 0 && (
                                        <p className="text-xs text-gray-500">No other items in this restaurant yet.</p>
                                    )}
                                </div>
                            </div>

                            <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-2">
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={itemForm.hasVariants}
                                           onChange={(e) => setItemForm({...itemForm, hasVariants: e.target.checked})}/>
                                    <span>Has Variants</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={itemForm.isAvailable}
                                           onChange={(e) => setItemForm({...itemForm, isAvailable: e.target.checked})}/>
                                    <span>Available</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={itemForm.isRecommended}
                                           data-testid="item-form-recommended"
                                           onChange={(e) => setItemForm({
                                               ...itemForm,
                                               isRecommended: e.target.checked
                                           })}/>
                                    <span>Recommended</span>
                                </label>
                                {/*/////////*/}
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={itemForm.catOffer}
                                           onChange={(e) => setItemForm({...itemForm, catOffer: e.target.checked})}/>
                                    <span>Category Offers</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={itemForm.storenintynine}
                                           onChange={(e) => setItemForm({
                                               ...itemForm,
                                               storenintynine: e.target.checked
                                           })}/>
                                    <span>99 Store</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={itemForm.storetwofifty}
                                           onChange={(e) => setItemForm({
                                               ...itemForm,
                                               storetwofifty: e.target.checked
                                           })}/>
                                    <span>250 store</span>
                                </label>
                                <label
                                    className="flex items-center gap-2"
                                    title="Untick to stop coupons/offers from discounting this item"
                                >
                                    <input type="checkbox" checked={itemForm.couponApplicable}
                                           data-testid="item-form-coupon-applicable"
                                           onChange={(e) => setItemForm({
                                               ...itemForm,
                                               couponApplicable: e.target.checked
                                           })}/>
                                    <span>Coupon Applicable</span>
                                </label>
                                <label
                                    className="flex items-center gap-2"
                                    title="Tick only if this dish is prepared with NO onion and NO garlic — it will then appear in the 'Without Onion & Garlic' collection in the app"
                                >
                                    <input type="checkbox" checked={itemForm.noOnionGarlic}
                                           data-testid="item-form-no-onion-garlic"
                                           onChange={(e) => setItemForm({
                                               ...itemForm,
                                               noOnionGarlic: e.target.checked
                                           })}/>
                                    <span>Without Onion &amp; Garlic</span>
                                </label>
                                {/*/////////*/}


                            </div>
                        </div>

                        <Button type="submit" className="w-full" style={{backgroundColor: '#C2410C'}}>
                            {currentItem ? 'Update Item' : 'Create Item'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Item Details Dialog with Variants & Addons */}
            <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selectedItem?.name}</DialogTitle>
                    </DialogHeader>

                    {selectedItem && (
                        <div className="space-y-6">
                            {/* Variants Section */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold">Variants
                                        ({selectedItem.variants?.length || 0})</h3>
                                    <Button size="sm" onClick={() => setShowVariantDialog(true)}
                                            style={{backgroundColor: '#C2410C'}}>
                                        <Plus size={16} className="mr-1"/> Add Variant
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {selectedItem.variants?.map((variant) => (
                                        <div key={variant.id}
                                             className="flex justify-between items-center p-3 border rounded-lg">
                                            <div>
                                                <p className="font-medium">{variant.title}</p>
                                                <p className="text-sm text-gray-600">Restaurant price: <span className="font-medium text-gray-900">{formatRupee(variant.price)}</span>{variant.mrpPrice ? ` (MRP: ${formatRupee(variant.mrpPrice)})` : ''}</p>
                                                <p className="text-xs text-gray-500">Customer price: {formatRupee(customerPrice(variant.price, commissionFor(selectedItem)))}</p>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => deleteVariant(variant.id)}>
                                                <Trash2 size={16} className="text-red-600"/>
                                            </Button>
                                        </div>
                                    ))}
                                    {(!selectedItem.variants || selectedItem.variants.length === 0) && (
                                        <p className="text-gray-500 text-sm">No variants added</p>
                                    )}
                                </div>
                            </div>

                            {/* Addons Section */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold">Addons
                                        ({selectedItem.addons?.length || 0})</h3>
                                    <Button size="sm" onClick={() => setShowAddonDialog(true)}
                                            style={{backgroundColor: '#C2410C'}}>
                                        <Plus size={16} className="mr-1"/> Add Addon
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {selectedItem.addons?.map((addon) => (
                                        <div key={addon.id}
                                             className="flex justify-between items-center p-3 border rounded-lg">
                                            <div>
                                                <p className="font-medium">{addon.title}</p>
                                                <p className="text-sm text-gray-600">Restaurant price: <span className="font-medium text-gray-900">{formatRupee(addon.price)}</span>{addon.mrpPrice ? ` (MRP: ${formatRupee(addon.mrpPrice)})` : ''}</p>
                                                <p className="text-xs text-gray-500">Customer price: {formatRupee(customerPrice(addon.price, commissionFor(selectedItem)))}</p>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => deleteAddon(addon.id)}>
                                                <Trash2 size={16} className="text-red-600"/>
                                            </Button>
                                        </div>
                                    ))}
                                    {(!selectedItem.addons || selectedItem.addons.length === 0) && (
                                        <p className="text-gray-500 text-sm">No addons added</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Variant Dialog */}
            <Dialog open={showVariantDialog} onOpenChange={setShowVariantDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Variant</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleVariantSubmit} className="space-y-4">
                        <div>
                            <Label>Variant Name *</Label>
                            <Input value={variantForm.title}
                                   onChange={(e) => setVariantForm({...variantForm, title: e.target.value})}
                                   placeholder="e.g., Small, Medium, Large" required/>
                        </div>
                        <div>
                            <Label>Food Type</Label>
                            <Select value={variantForm.foodType}
                                    onValueChange={(value) => setVariantForm({...variantForm, foodType: value})}>
                                <SelectTrigger>
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="VEG">Veg</SelectItem>
                                    <SelectItem value="NONVEG">Non-Veg</SelectItem>
                                    <SelectItem value="EGG">Egg</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Restaurant Price *</Label>
                                <Input type="number" step="0.01" value={variantForm.price}
                                       onChange={(e) => setVariantForm({...variantForm, price: e.target.value})}
                                       required/>
                            </div>
                            <div>
                                <Label>MRP Price</Label>
                                <Input type="number" step="0.01" value={variantForm.mrpPrice}
                                       onChange={(e) => setVariantForm({...variantForm, mrpPrice: e.target.value})}/>
                            </div>
                        </div>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={variantForm.isDefault}
                                   onChange={(e) => setVariantForm({...variantForm, isDefault: e.target.checked})}/>
                            <span>Set as default</span>
                        </label>
                        <Button type="submit" className="w-full" style={{backgroundColor: '#C2410C'}}>Add
                            Variant</Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Add Addon Dialog */}
            <Dialog open={showAddonDialog} onOpenChange={setShowAddonDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Addon</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddonSubmit} className="space-y-4">
                        <div>
                            <Label>Addon Name *</Label>
                            <Input value={addonForm.title}
                                   onChange={(e) => setAddonForm({...addonForm, title: e.target.value})}
                                   placeholder="e.g., Extra Cheese, Drink" required/>
                        </div>
                        <div>
                            <Label>Food Type</Label>
                            <Select value={addonForm.foodType}
                                    onValueChange={(value) => setAddonForm({...addonForm, foodType: value})}>
                                <SelectTrigger>
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="VEG">Veg</SelectItem>
                                    <SelectItem value="NONVEG">Non-Veg</SelectItem>
                                    <SelectItem value="EGG">Egg</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Restaurant Price *</Label>
                                <Input type="number" step="0.01" value={addonForm.price}
                                       onChange={(e) => setAddonForm({...addonForm, price: e.target.value})} required/>
                            </div>
                            <div>
                                <Label>MRP Price</Label>
                                <Input type="number" step="0.01" value={addonForm.mrpPrice}
                                       onChange={(e) => setAddonForm({...addonForm, mrpPrice: e.target.value})}/>
                            </div>
                        </div>
                        <Button type="submit" className="w-full" style={{backgroundColor: '#C2410C'}}>Add Addon</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ItemsTab;
